import clsx from 'clsx';
import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { IoCheckmarkOutline } from 'react-icons/io5';

import { TASK_STATUSES, TaskStatus } from '@/store/tasks/tasks.dto';

import styles from './TaskStatusesBadge.module.scss';

type TaskStatusesBadgeProps = {
  status: TaskStatus;
  className?: string;
  onChange?: (status: TaskStatus) => void;
  isServiceProvider?: boolean;
  /**
   * Which statuses the dropdown offers. Defaults to the full owner set; the maintenance
   * surfaces pass TODO_DONE_STATUSES, where a job is only ever to do or done.
   */
  options?: readonly TaskStatus[];
};

type StatusConfig = { label: string; className: string; showIcon: boolean };

const STATUS_OPTIONS = [
  TaskStatus.PENDING,
  TaskStatus.IN_PROGRESS,
  TaskStatus.ON_APPROVAL,
  TaskStatus.COMPLETED,
];

const statusLabel = (status: TaskStatus) =>
  TASK_STATUSES.find((option) => option.id === status)?.label ?? status;

const OWNER_STATUS_CONFIG: Record<string, StatusConfig> = {
  [TaskStatus.PENDING]: {
    label: statusLabel(TaskStatus.PENDING),
    className: styles.todo,
    showIcon: false,
  },
  [TaskStatus.IN_PROGRESS]: {
    label: statusLabel(TaskStatus.IN_PROGRESS),
    className: styles.inProgress,
    showIcon: false,
  },
  [TaskStatus.ON_APPROVAL]: {
    label: statusLabel(TaskStatus.ON_APPROVAL),
    className: styles.onReview,
    showIcon: false,
  },
  [TaskStatus.COMPLETED]: {
    label: statusLabel(TaskStatus.COMPLETED),
    className: styles.done,
    showIcon: true,
  },
};

const SERVICE_PROVIDER_STATUS_CONFIG: Record<string, StatusConfig> = {
  [TaskStatus.PENDING]: OWNER_STATUS_CONFIG[TaskStatus.PENDING],
  [TaskStatus.IN_PROGRESS]: OWNER_STATUS_CONFIG[TaskStatus.IN_PROGRESS],
  [TaskStatus.ON_APPROVAL]: OWNER_STATUS_CONFIG[TaskStatus.ON_APPROVAL],
};

const TaskStatusesBadge = ({
  status,
  className,
  onChange,
  isServiceProvider,
  options = STATUS_OPTIONS,
}: TaskStatusesBadgeProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const [coords, setCoords] = useState<{
    top: number;
    left: number;
    width: number;
    align: 'left' | 'right';
  }>({ top: 0, left: 0, width: 0, align: 'left' });
  const isSelectable = !!onChange;
  const STATUS_CONFIG = isServiceProvider ? SERVICE_PROVIDER_STATUS_CONFIG : OWNER_STATUS_CONFIG;

  const calculatePosition = () => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const shouldAlignRight = rect.left + 200 > window.innerWidth;

      return {
        top: rect.bottom + window.scrollY + 4,
        left: shouldAlignRight ? rect.right + window.scrollX : rect.left + window.scrollX,
        width: rect.width,
        align: (shouldAlignRight ? 'right' : 'left') as 'left' | 'right',
      };
    }
    return { top: 0, left: 0, width: 0, align: 'left' as const };
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleUpdatePosition = () => {
      if (isOpen) {
        setCoords(calculatePosition());
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      window.addEventListener('scroll', handleUpdatePosition, true);
      window.addEventListener('resize', handleUpdatePosition);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      window.removeEventListener('scroll', handleUpdatePosition, true);
      window.removeEventListener('resize', handleUpdatePosition);
    };
  }, [isOpen]);

  const handleSelect = (newStatus: TaskStatus) => {
    onChange?.(newStatus);
    setIsOpen(false);
  };

  const toggleOpen = (e: React.MouseEvent) => {
    if (!isSelectable) return;

    e.stopPropagation();

    if (!isOpen) {
      setCoords(calculatePosition());
    }

    setIsOpen(!isOpen);
  };

  return (
    <div ref={wrapperRef} className={clsx(styles.wrapper, className)}>
      <div
        className={clsx(
          styles.badge,
          STATUS_CONFIG[status]?.className,
          isSelectable && styles.selectable,
        )}
        onClick={toggleOpen}
        role={isSelectable ? 'button' : undefined}
        tabIndex={isSelectable ? 0 : undefined}
      >
        <span className={styles.text}>{STATUS_CONFIG[status]?.label}</span>
        {STATUS_CONFIG[status]?.showIcon && <IoCheckmarkOutline className={styles.icon} />}
      </div>

      {isOpen &&
        createPortal(
          <ul
            ref={dropdownRef}
            className={styles.dropdown}
            style={{
              position: 'absolute',
              top: coords.top,
              left: coords.left,
              minWidth: coords.width,
              width: 'max-content',
              zIndex: 9999,
              transform: coords.align === 'right' ? 'translateX(-100%)' : 'none',
            }}
          >
            {options.map((option) => (
              <li
                key={option}
                className={clsx(styles.option, option === status && styles.selected)}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect(option);
                }}
              >
                <span className={clsx(styles.optionBadge, STATUS_CONFIG[option]?.className)}>
                  {STATUS_CONFIG[option]?.label}
                  {STATUS_CONFIG[option]?.showIcon && (
                    <IoCheckmarkOutline className={styles.icon} />
                  )}
                </span>
              </li>
            ))}
          </ul>,
          document.body,
        )}
    </div>
  );
};

export default TaskStatusesBadge;
